import React, {useState} from 'react'
import {Bar, Pie} from 'react-chartjs-2'
import {ChartOptions} from 'chart.js'
import {IComponent, ISection} from '../../../interfaces/Analytics/Analytics'
import moment from 'moment'
import {Dropdown} from 'rsuite'
import {useIntl} from 'react-intl'
import {useAnalyticsStore} from '../../../stores/Analytics/AnalyticStore'
import List from './List'
import {formatTimeByFrame, randomColor} from '../../../functions/general'
import clsx from 'clsx'
import useModalEffect from '../../../hooks/useModalEffect'
import PopupComponent from './PopupComponent'
import useUser from '../../../hooks/User'
import useAnalyticGuard from '../../../functions/Guard/analyticGuard'
import {useParams} from 'react-router-dom'
import SummaryWidget from './SummaryWidget'
import {AnimateLayoutChanges, defaultAnimateLayoutChanges, useSortable} from '@dnd-kit/sortable'
import {CSS} from '@dnd-kit/utilities'

interface IProps {
  component: IComponent
  section?: ISection
  isPreview?: boolean
  isDashboard?: boolean
  modeDND?: boolean
}

export default function ComponentAnalytics({
  component,
  section,
  isPreview,
  isDashboard,
  modeDND,
}: IProps) {
  const intl = useIntl()
  const {templateAnalyticId} = useParams()
  const {
    setComponent,
    setShowUpdateComponentModal,
    setSection,
    setShowDeleteComponentModal,
    component: componentFromStore,
    setIsComponentCopyCase,
    templateAnalytic,
    setShowInstallAnalyticDashboardModal,
  } = useAnalyticsStore()
  const user = useUser()
  const {isCanEditTemplate, isOwnerTemplate} = useAnalyticGuard(user)
  const animateLayoutChanges: AnimateLayoutChanges = (args) =>
    defaultAnimateLayoutChanges({...args, wasDragging: true})
  const {
    active,
    attributes,
    isDragging,
    isSorting,
    listeners,
    overIndex,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
  } = useSortable({
    id: component.componentId,
    animateLayoutChanges,
    data: {
      type: 'component',
    },
  })
  const style = {
    transform: CSS.Translate.toString(transform),
    ...(isSorting && {transition}),
  }

  const pieConfig = React.useMemo(() => {
    if (component.chartType !== 'PIE') return null

    const labels = component?.dataCore[0]?.data?.map((d: any) =>
      formatTimeByFrame(d.time, component.timeFrame)
    )

    const datasets = component.dataCore.map((core) => {
      const randomColors = core.data.map(() => randomColor())
      return {
        label: core.name,
        data: core.data.map((d) => d.value),
        backgroundColor: randomColors,
      }
    })

    const options: ChartOptions<'pie'> = {
      responsive: true,
      plugins: {
        legend: {
          display: true,
        },
      },
    }

    return {
      labels,
      datasets,
      options,
    }
  }, [component])

  const barConfig = React.useMemo(() => {
    if (component.chartType !== 'BAR') return null

    const labels = component?.dataCore[0]?.data?.map((d: any) =>
      formatTimeByFrame(d.time, component.timeFrame)
    )

    const datasets = component.dataCore.map((core) => {
      return {
        label: core.name,
        backgroundColor: core.colorCode,
        data: core.data.map((d) => d.value),
      }
    })

    const options: ChartOptions<'bar'> = {
      responsive: true,
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: true,
          text: component.nameComponent,
        },
        zoom: {
          pan: {
            enabled: true,
            mode: 'x',
            modifierKey: 'ctrl',
          },
          zoom: {
            drag: {
              enabled: true,
            },
            mode: 'x',
          },
        },
      },
    }

    return {
      labels,
      datasets,
      options,
    }
  }, [component])

  const chartRef = React.useRef(null)

  const renderIconButtonSection = (props: any, ref: any) => {
    return (
      <button {...props} ref={ref} className='btn btn-sm btn-icon btn-light'>
        <i className={`fas fs-8 fa-ellipsis-v`}></i>
      </button>
    )
  }

  const [showPopupComponent, setShowPopupComponent] = useState(false)

  useModalEffect({
    showModal: showPopupComponent,
    setShowModal: setShowPopupComponent,
    modalId: `popup-component-${component.componentId}`,
  })

  return (
    <div className={clsx("mb-3",'col-12 col-md-12 col-lg-12 col-xl-6', isPreview && "col-xl-12")}>
    <div
      className={clsx(
        'card position-relative',
        isDragging && 'opacity-50',
        component.chartType === 'SUMMARY' && 'text-white'
      )}
      ref={setNodeRef}
      style={{
        ...style,
        ...(component.chartType === 'SUMMARY' && {
          backgroundColor: component.dataCore[0]?.colorCode,
        }),
      }}
      id={`component-${component.componentId}`}
    >
      <div className='card-body p-4 d-flex justify-content-between flex-column'>
        <div className='position-absolute top-0 end-0 m-3 d-flex gap-2'>
          {component.chartType === 'BAR' && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                if (chartRef && chartRef.current) {
                  ;(chartRef.current as any).resetZoom()
                }
              }}
              className='btn btn-sm btn-icon btn-light'
            >
              <i className='bi bi-zoom-out'></i>
            </button>
          )}
          {!isPreview && !isDashboard && (
            <Dropdown renderToggle={renderIconButtonSection} placement='bottomEnd'>
              {(isCanEditTemplate(templateAnalyticId || '') ||
                (templateAnalytic && isOwnerTemplate(templateAnalytic))) && (
                <>
                  <Dropdown.Item
                    onClick={() => {
                      component.dataCore.forEach((core) => {
                        const coreTypeRule = core.rules.find((r) => r.typeFilter === 'coreType')
                        if (coreTypeRule) core.coreType = coreTypeRule.valueFilter as string
                      })
                      setComponent(component)
                      setSection(section!)
                      setShowUpdateComponentModal(true)
                    }}
                  >
                    {intl.formatMessage({id: 'FORM.ACTION.UPDATE'})}
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => {
                      setComponent(component)
                      setShowDeleteComponentModal(true)
                    }}
                  >
                    {intl.formatMessage({id: 'FORM.ACTION.DELETE'})}
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => {
                      component.dataCore.forEach((core) => {
                        const coreTypeRule = core.rules.find((r) => r.typeFilter === 'coreType')
                        if (coreTypeRule) core.coreType = coreTypeRule.valueFilter as string
                      })
                      setComponent(component)
                      setSection(section!)
                      setShowUpdateComponentModal(true)
                      setIsComponentCopyCase(true)
                    }}
                  >
                    {intl.formatMessage({id: 'FORM.ACTION.COPY'})}
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => {
                      setShowInstallAnalyticDashboardModal(true)
                      setComponent(component)
                    }}
                  >
                    {intl.formatMessage({id: 'FORM.ACTION.INSTALL_ON_DASHBOARD'})}
                  </Dropdown.Item>
                </>
              )}
              <Dropdown.Item
                onClick={() => {
                  setShowPopupComponent(true)
                  setComponent(component)
                }}
              >
                {intl.formatMessage({id: 'FORM.ACTION.DETAIL'})}
              </Dropdown.Item>
            </Dropdown>
          )}
          {(isCanEditTemplate(templateAnalyticId || '') ||
            (templateAnalytic && isOwnerTemplate(templateAnalytic))) &&
            !isPreview &&
            !isDashboard && (
              <div className='btn btn-sm btn-icon btn-light' {...attributes} {...listeners}>
                <i className='bi bi-grip-vertical'></i>
              </div>
            )}
          {isDashboard && modeDND && (
            <div className='btn btn-sm btn-icon btn-light' {...attributes} {...listeners}>
              <i className='bi bi-grip-vertical'></i>
            </div>
          )}
        </div>

        <div className='d-flex flex-stack flex-wrap flex-grow-1 px-9 pt-9 pb-3'>
          <div className='me-2'>
            <span
              className={clsx(
                'fw-bolder d-block fs-3',
                component.chartType === 'SUMMARY' ? 'text-white' : 'text-gray-800'
              )}
            >
              {component.nameComponent}
            </span>
            <span
              className={clsx(
                'fw-bold',
                component.chartType === 'SUMMARY' ? 'text-white' : 'text-gray-400'
              )}
            >
              {moment(component.startDate).format('DD MMMM YYYY')} -{' '}
              {moment(component.endDate).format('DD MMMM YYYY')}
            </span>
          </div>
        </div>

        {component.chartType === 'SUMMARY' && (
          <div className='overflow-auto px-9' id={`component-${component.componentId}-export`}
          style={{
            ...(component.chartType === 'SUMMARY' && {
              backgroundColor: component.dataCore[0]?.colorCode,
            }),
          }}> 
            <SummaryWidget component={component} />
          </div>
        )}
        {component.chartType === 'PIE' && pieConfig && (
          <div className='overflow-auto' id={`component-${component.componentId}-export`}>
            <div style={{minWidth: '500px', minHeight: '300px'}}>
              <Pie
                options={pieConfig.options}
                data={{labels: pieConfig.labels, datasets: pieConfig.datasets}}
                style={{maxHeight: '400px'}}
              />
            </div>
          </div>
        )}
        {component.chartType === 'BAR' && barConfig && (
          <div className='overflow-auto' id={`component-${component.componentId}-export`}>
            <div style={{minWidth: '500px', minHeight: '300px'}}>
              <Bar
                ref={chartRef}
                options={barConfig.options}
                data={{labels: barConfig.labels, datasets: barConfig.datasets}}
                style={{maxHeight: '400px'}}
              />
            </div>
          </div>
        )}
        {component.chartType === 'LIST' && (
          <div id={`component-${component.componentId}-export`}>
            <List component={component} usePagination={true} />
          </div>
        )}
      </div>

      {showPopupComponent && <PopupComponent component={componentFromStore!} />}
    </div>
    </div>
  )
}
